import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  ChevronDown,
  ChevronRight,
  Filter,
  History,
  IndianRupee,
  RefreshCw,
  Search,
  Target,
  TrendingDown,
  TrendingUp,
  User,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../slices';
import {
  clearError,
  clearSuccess,
  fetchBetHistory,
  setFilters,
} from '../../slices/betHistory/betHistorySlice';
import {
  BetHistory as BetHistoryType,
  PlacedBet,
} from '../../slices/betHistory/betHistorySlice';

const MarketBetHistory = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    bets,
    summary,
    pagination,
    loading,
    error,
    success,
  } = useSelector((state: RootState) => state.BetHistory);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedBetId, setExpandedBetId] = useState<number | null>(null);

  const [filterData, setFilterData] = useState({
    market_id: '',
    bet_type_id: '',
    bet_status: '',
    session_type: '',
    user_id: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    dispatch(
      fetchBetHistory({
        page: currentPage,
        search: searchTerm,
        ...filterData,
      }),
    );
  }, [dispatch, currentPage, searchTerm, filterData]);

  useEffect(() => {
    if (!success) return;

    const timer = setTimeout(() => {
      dispatch(clearSuccess());
    }, 5000);

    return () => clearTimeout(timer);
  }, [success, dispatch]);

  useEffect(() => {
    if (!error) return;

    const timer = setTimeout(() => {
      dispatch(clearError());
    }, 5000);

    return () => clearTimeout(timer);
  }, [error, dispatch]);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatCurrency = (amount: number | null | undefined) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));

  const displayValue = (
    value: string | number | null | undefined,
    fallback = '—',
  ) => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    return String(value);
  };

  const getStatusBadge = (status?: string) => {
    switch ((status || '').toLowerCase()) {
      case 'won':
        return 'bg-green-100 text-green-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProfitColor = (amount: number) => {
    if (amount > 0) return 'text-green-600';
    if (amount < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getSessionBadge = (session?: string | null) => {
    const normalized = (session || '').toLowerCase();

    if (normalized === 'open') {
      return 'bg-blue-100 text-blue-800';
    }

    if (normalized === 'close') {
      return 'bg-purple-100 text-purple-800';
    }

    return 'bg-gray-100 text-gray-700';
  };

  const getSessionAwareSelection = (
    bet: BetHistoryType,
    field: 'open_pana' | 'open_digit' | 'close_pana' | 'close_digit',
  ) => {
    const session = (bet.bet_details?.session || '').toLowerCase();

    if (session === 'open' && field.startsWith('close_')) {
      return '—';
    }

    if (session === 'close' && field.startsWith('open_')) {
      return '—';
    }

    return displayValue(bet.bet_details?.[field]);
  };

  const getSelectionFromPlacedBet = (placedBet: PlacedBet) => {
    const combination = placedBet.combination;
    if (combination !== null && combination !== undefined && combination !== '') {
      return String(combination);
    }

    const jodi = placedBet.jodi;
    if (jodi !== null && jodi !== undefined && jodi !== '') {
      return String(jodi);
    }

    const openPana = placedBet.open_pana ?? placedBet.pana;
    const closePana = placedBet.close_pana;
    const openDigit = placedBet.open_digit ?? placedBet.digit;
    const closeDigit = placedBet.close_digit;

    if (
      openPana !== null &&
      openPana !== undefined &&
      openPana !== '' &&
      closePana !== null &&
      closePana !== undefined &&
      closePana !== ''
    ) {
      return `${openPana}-${closePana}`;
    }

    if (
      openDigit !== null &&
      openDigit !== undefined &&
      openDigit !== '' &&
      closePana !== null &&
      closePana !== undefined &&
      closePana !== ''
    ) {
      return `${openDigit}-${closePana}`;
    }

    if (
      openPana !== null &&
      openPana !== undefined &&
      openPana !== '' &&
      closeDigit !== null &&
      closeDigit !== undefined &&
      closeDigit !== ''
    ) {
      return `${openPana}-${closeDigit}`;
    }

    return displayValue(
      placedBet.pana ??
        placedBet.digit ??
        placedBet.open_pana ??
        placedBet.close_pana ??
        placedBet.open_digit ??
        placedBet.close_digit,
    );
  };

  const getSelectedNumber = (bet: BetHistoryType) => {
    const details = bet.bet_details;

    // Penal Group selections are stored authoritatively inside
    // result_data.original_payload.bets[].pana. Some API responses do
    // not expose those entries through placed_bets, which previously
    // caused the main Bet History row to display an empty dash even
    // though the selected panas were stored correctly.
    if (
      String(bet.bet_type?.name || '').trim().toLowerCase() ===
      'penal group'
    ) {
      const resultData = details.result_data as any;
      const originalBets =
        resultData?.original_payload?.bets;

      if (Array.isArray(originalBets)) {
        const panas = originalBets
          .map((placedBet: any) => placedBet?.pana)
          .filter(
            (value: any) =>
              value !== null &&
              value !== undefined &&
              value !== '',
          );

        if (panas.length) {
          return panas.map(String).join(', ');
        }
      }

      const processedPanas =
        resultData?.processed_result?.panas;

      if (
        Array.isArray(processedPanas) &&
        processedPanas.length
      ) {
        return processedPanas
          .filter(
            (value: any) =>
              value !== null &&
              value !== undefined &&
              value !== '',
          )
          .map(String)
          .join(', ');
      }
    }

    if (details.placed_bets?.length) {
      const selections = details.placed_bets
        .map(getSelectionFromPlacedBet)
        .filter((value) => value !== '—');

      if (selections.length) {
        return selections.join(', ');
      }
    }

    if (
      details.jodi !== null &&
      details.jodi !== undefined &&
      details.jodi !== ''
    ) {
      return String(details.jodi);
    }

    const openPana = details.open_pana;
    const closePana = details.close_pana;
    const openDigit = details.open_digit;
    const closeDigit = details.close_digit;

    if (
      openPana !== null &&
      openPana !== undefined &&
      openPana !== '' &&
      closePana !== null &&
      closePana !== undefined &&
      closePana !== ''
    ) {
      return `${openPana}-${closePana}`;
    }

    if (
      openDigit !== null &&
      openDigit !== undefined &&
      openDigit !== '' &&
      closePana !== null &&
      closePana !== undefined &&
      closePana !== ''
    ) {
      return `${openDigit}-${closePana}`;
    }

    if (
      openPana !== null &&
      openPana !== undefined &&
      openPana !== '' &&
      closeDigit !== null &&
      closeDigit !== undefined &&
      closeDigit !== ''
    ) {
      return `${openPana}-${closeDigit}`;
    }

    return displayValue(
      details.open_pana ??
        details.close_pana ??
        details.open_digit ??
        details.close_digit,
    );
  };

  const getDeclaredResult = (bet: BetHistoryType) => {
    const market = bet.market;

    if (
      market.open_pana &&
      market.open_digit &&
      market.close_pana &&
      market.close_digit
    ) {
      return `${market.open_pana}-${market.open_digit} | ${market.close_pana}-${market.close_digit}`;
    }

    if (market.open_pana && market.open_digit) {
      return `${market.open_pana}-${market.open_digit}`;
    }

    if (market.close_pana && market.close_digit) {
      return `${market.close_pana}-${market.close_digit}`;
    }

    return market.jodi_digit || 'Not declared';
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setCurrentPage(1);
    dispatch(setFilters({ search: searchTerm }));
  };

  const handleFilterChange = (key: string, value: string) => {
    setCurrentPage(1);
    setFilterData((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    dispatch(setFilters({ ...filterData }));
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      market_id: '',
      bet_type_id: '',
      bet_status: '',
      session_type: '',
      user_id: '',
      date_from: '',
      date_to: '',
    };

    setFilterData(clearedFilters);
    setSearchTerm('');
    setCurrentPage(1);
    setExpandedBetId(null);

    dispatch(
      setFilters({
        search: '',
        ...clearedFilters,
      }),
    );
  };

  const handleRefresh = () => {
    dispatch(
      fetchBetHistory({
        page: currentPage,
        search: searchTerm,
        ...filterData,
      }),
    );
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setExpandedBetId(null);
  };

  if (loading && (!bets || bets.length === 0)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-20 w-20 animate-spin rounded-full border-b-2 border-blue-500" />
          <p className="text-gray-600">Loading market bid history...</p>
        </div>
      </div>
    );
  }

  if (error && (!bets || bets.length === 0)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-xl text-red-500">Error: {error}</div>
          <button
            type="button"
            onClick={handleRefresh}
            className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-3">
          <History className="h-8 w-8 text-blue-500" />
          <h1 className="text-3xl font-bold text-gray-900">
            Market User Bid History
          </h1>
        </div>
        <p className="text-gray-600">
          Review regular Market bets, sessions, selected numbers and settlement status.
        </p>
      </div>

      {summary && (
        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Bets</p>
                <p className="text-2xl font-bold text-gray-900">
                  {summary.total_bets}
                </p>
              </div>
              <div className="rounded-full bg-orange-100 p-3">
                <BarChart3 className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Win Rate</p>
                <p className="text-2xl font-bold text-green-600">
                  {Number(summary.win_rate || 0).toFixed(1)}%
                </p>
              </div>
              <div className="rounded-full bg-green-100 p-3">
                <Target className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Wagered</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(summary.total_wagered)}
                </p>
              </div>
              <div className="rounded-full bg-purple-100 p-3">
                <IndianRupee className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Net Profit</p>
                <p
                  className={`text-2xl font-bold ${getProfitColor(
                    Number(summary.net_profit || 0),
                  )}`}
                >
                  {formatCurrency(summary.net_profit)}
                </p>
              </div>
              <div className="rounded-full bg-orange-100 p-3">
                {Number(summary.net_profit || 0) >= 0 ? (
                  <TrendingUp className="h-6 w-6 text-green-600" />
                ) : (
                  <TrendingDown className="h-6 w-6 text-red-600" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 rounded-lg border bg-white p-6 shadow-sm">
        <div className="mb-4 flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
          <form onSubmit={handleSearch} className="flex w-full gap-2 lg:w-auto">
            <div className="relative flex-1 lg:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search user, mobile, market or bet..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              type="submit"
              className="rounded-lg bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
            >
              Search
            </button>
          </form>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowFilters((visible) => !visible)}
              className="flex items-center gap-2 rounded-lg bg-gray-500 px-4 py-2 text-white transition-colors hover:bg-gray-600"
            >
              <Filter className="h-4 w-4" />
              Filters
            </button>

            <button
              type="button"
              onClick={handleRefresh}
              className="flex items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-white transition-colors hover:bg-green-600"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="border-t pt-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Session
                </label>
                <select
                  value={filterData.session_type}
                  onChange={(event) =>
                    handleFilterChange('session_type', event.target.value)
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Sessions</option>
                  <option value="open">Open</option>
                  <option value="close">Close</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Bet Status
                </label>
                <select
                  value={filterData.bet_status}
                  onChange={(event) =>
                    handleFilterChange('bet_status', event.target.value)
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Date From
                </label>
                <input
                  type="date"
                  value={filterData.date_from}
                  onChange={(event) =>
                    handleFilterChange('date_from', event.target.value)
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Date To
                </label>
                <input
                  type="date"
                  value={filterData.date_to}
                  onChange={(event) =>
                    handleFilterChange('date_to', event.target.value)
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={handleApplyFilters}
                className="rounded-lg bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
              >
                Apply Filters
              </button>

              <button
                type="button"
                onClick={handleClearFilters}
                className="rounded-lg bg-gray-500 px-4 py-2 text-white transition-colors hover:bg-gray-600"
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12 px-4 py-3" />
                {[
                  'ID',
                  'User',
                  'Market',
                  'Session',
                  'Game Type',
                  'Selected Number',
                  'Bet Amount',
                  'Potential Win',
                  'Status',
                  'Date',
                ].map((heading) => (
                  <th
                    key={heading}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 bg-white">
              {bets && bets.length > 0 ? (
                bets.map((bet) => {
                  const expanded = expandedBetId === bet.id;

                  return (
                    <React.Fragment key={bet.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <button
                            type="button"
                            aria-label={expanded ? 'Collapse bet details' : 'Expand bet details'}
                            onClick={() =>
                              setExpandedBetId(expanded ? null : bet.id)
                            }
                            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                          >
                            {expanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-900">
                          {bet.id}
                        </td>

                        <td className="whitespace-nowrap px-4 py-4">
                          <div className="flex items-center">
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-orange-100">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>

                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {bet.user?.first_name || 'Unknown User'}
                              </div>
                              <div className="text-sm text-gray-500">
                                {bet.user?.mobile_number || '—'}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {bet.market?.name || 'Unknown Market'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {bet.market?.current_status || '—'}
                          </div>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4">
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold uppercase ${getSessionBadge(
                              bet.bet_details?.session,
                            )}`}
                          >
                            {displayValue(bet.bet_details?.session, 'Unknown')}
                          </span>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4">
                          <div className="text-sm text-gray-900">
                            {bet.bet_type?.name || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {bet.app_bet_type?.payout_multiplier
                              ? `${bet.app_bet_type.payout_multiplier}x`
                              : '—'}
                          </div>
                        </td>

                        <td className="max-w-xs px-4 py-4 text-sm font-semibold text-gray-900">
                          <div className="max-w-xs truncate" title={getSelectedNumber(bet)}>
                            {getSelectedNumber(bet)}
                          </div>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-900">
                          {formatCurrency(bet.bet_details?.amount)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-900">
                          {formatCurrency(bet.bet_details?.potential_payout)}
                        </td>

                        <td className="whitespace-nowrap px-4 py-4">
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold capitalize ${getStatusBadge(
                              bet.bet_details?.bet_status,
                            )}`}
                          >
                            {bet.bet_details?.bet_status || 'Unknown'}
                          </span>
                        </td>

                        <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                          {formatDate(bet.timestamps.created_at)}
                        </td>
                      </tr>

                      {expanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={11} className="px-6 py-5">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-lg border bg-white p-4">
                                <p className="mb-3 text-sm font-semibold text-gray-900">
                                  Bet Selection
                                </p>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Open Pana</span>
                                    <span className="font-medium">
                                      {getSessionAwareSelection(bet, 'open_pana')}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Open Digit</span>
                                    <span className="font-medium">
                                      {getSessionAwareSelection(bet, 'open_digit')}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Close Pana</span>
                                    <span className="font-medium">
                                      {getSessionAwareSelection(bet, 'close_pana')}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Close Digit</span>
                                    <span className="font-medium">
                                      {getSessionAwareSelection(bet, 'close_digit')}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Jodi</span>
                                    <span className="font-medium">
                                      {displayValue(bet.bet_details.jodi)}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-lg border bg-white p-4">
                                <p className="mb-3 text-sm font-semibold text-gray-900">
                                  Wallet Details
                                </p>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Before Bet</span>
                                    <span className="font-medium">
                                      {formatCurrency(bet.bet_details.before_amount)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">After Bet</span>
                                    <span className="font-medium">
                                      {formatCurrency(bet.bet_details.after_amount)}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Win Amount</span>
                                    <span className="font-medium">
                                      {formatCurrency(
                                        bet.calculated_values?.win_amount,
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Net Profit</span>
                                    <span
                                      className={`font-medium ${getProfitColor(
                                        Number(
                                          bet.calculated_values?.net_profit || 0,
                                        ),
                                      )}`}
                                    >
                                      {formatCurrency(
                                        bet.calculated_values?.net_profit,
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-lg border bg-white p-4">
                                <p className="mb-3 text-sm font-semibold text-gray-900">
                                  Declared Market Result
                                </p>
                                <p className="break-words text-lg font-bold text-gray-900">
                                  {getDeclaredResult(bet)}
                                </p>
                                <p className="mt-2 text-sm text-gray-500">
                                  Current result stored against this market.
                                </p>
                              </div>

                              <div className="rounded-lg border bg-white p-4">
                                <p className="mb-3 text-sm font-semibold text-gray-900">
                                  Placed Bets
                                </p>

                                {bet.bet_details.placed_bets?.length ? (
                                  <div className="max-h-40 space-y-2 overflow-y-auto">
                                    {bet.bet_details.placed_bets.map(
                                      (placedBet, index) => (
                                        <div
                                          key={`${bet.id}-placed-${index}`}
                                          className="rounded border bg-gray-50 px-3 py-2 text-sm"
                                        >
                                          <div className="flex justify-between gap-3">
                                            <span className="font-medium text-gray-900">
                                              {getSelectionFromPlacedBet(placedBet)}
                                            </span>
                                            <span className="text-gray-600">
                                              {placedBet.points !== null &&
                                              placedBet.points !== undefined
                                                ? formatCurrency(
                                                    Number(placedBet.points),
                                                  )
                                                : '—'}
                                            </span>
                                          </div>
                                          <div className="mt-1 text-xs uppercase text-gray-500">
                                            {displayValue(
                                              placedBet.bet_type,
                                              bet.bet_details.session || 'Unknown',
                                            )}
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500">
                                    No detailed placed-bet entries available.
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11} className="px-6 py-12 text-center">
                    <History className="mx-auto mb-4 h-12 w-12 text-gray-300" />
                    <p className="text-lg font-medium text-slate-600">
                      No market bid history found
                    </p>
                    <p className="text-sm text-slate-500">
                      Try adjusting the search or filter criteria.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            <p className="hidden text-sm text-gray-700 sm:block">
              Showing{' '}
              <span className="font-medium">
                {(currentPage - 1) * pagination.page_size + 1}
              </span>{' '}
              to{' '}
              <span className="font-medium">
                {Math.min(
                  currentPage * pagination.page_size,
                  pagination.total_count,
                )}
              </span>{' '}
              of{' '}
              <span className="font-medium">{pagination.total_count}</span>{' '}
              results
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.has_previous}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>

              <span className="flex items-center px-3 text-sm text-gray-600">
                Page {pagination.current_page} of {pagination.total_pages}
              </span>

              <button
                type="button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.has_next}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {success && (
        <div className="fixed right-4 top-4 z-50 rounded border border-green-400 bg-green-100 px-4 py-3 text-green-700">
          {success}
        </div>
      )}

      {error && (
        <div className="fixed right-4 top-4 z-50 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
          {error}
        </div>
      )}
    </div>
  );
};

export default MarketBetHistory;
